type EasingFunction = (t: number) => number;
import { expoOut, linear, sineOut } from "svelte/easing";

// export const easeOutExpo = quintOut
export const easeOutExpo = (x: number) =>
  x === 1 ? 1 : 1 - Math.pow(2, -10 * x);

export interface SlideParams {
  delay?: number;
  duration?: number;
  easing?: EasingFunction;
  axis?: "x" | "y";
}

export const fadeUp = (
  node: Element,
  {
    delay = 0,
    duration = 300,
    easing = linear,
    distance = 150,
  } = {},
) => {
  const opacitySection = 0.65;
  const newDistance = distance / opacitySection;
  const delayFactor = (duration + delay) / duration; // Calculate delay factor

  return {
    delay: 0,
    duration,
    easing: easing,
    css: (t: number) => {
      const adjustedT = t / delayFactor;
      const translateY = newDistance * (1 - expoOut(adjustedT)) *
        1;
      const opacity = t > (1 - opacitySection)
        ? linear((t - (1 - opacitySection)) * (1 / opacitySection))
        : 0;
      return `transform: translateY(${translateY}px); opacity: ${opacity};`;
    },
  };
};

function t2(delay, t) {
  if (delay === 0) {
    return t;
  }
  if (t < delay) {
    return 0;
  }
  return (t - delay) / (1 - delay);
}

export const pageIn = (
  node: Element,
  {
    direction = "left",
    duration = 300,
    easingDistance = linear,
    easingOpacity = linear,
    distance = 40,
    start = 0.25,
  } = {},
) => {
  return {
    delay: 0,
    duration,
    css: (t: number) => {
      const adjustedTDistance = easingDistance(t2(start, t));
      const adjustedTOpacity = easingOpacity(t2(start, t));
      const translateX = t < start ? 0 : distance * (1 - adjustedTDistance) *
        (direction === "left" ? -1 : 1);
      return `transform: translateX(${translateX}px); opacity: ${adjustedTOpacity};`;
    },
  };
};

export const pageOut = (
  node: Element,
  {
    direction = "right",
    duration = 300,
    easingDistance = linear,
    easingOpacity = linear,
    distance = 20,
    absolute = true,
  } = {},
) => {
  return {
    duration,
    css: (t: number) => {
      const adjustedTDistance = easingDistance(t);
      const adjustedTOpacity = easingOpacity(t);
      const translateX = distance * (1 - adjustedTDistance) *
        (direction === "left" ? -1 : 1);
      const opacity = adjustedTOpacity;
      const result = `transform: translateX(${translateX}px);` +
        `opacity: ${opacity};`;
      return absolute ? `position: absolute; ${result}` : result;
    },
  };
};

export function slide(
  node,
  {
    delay = 0,
    duration = 200,
    easing = sineOut,
    axis = "y",
    overflow = "visible",
    adaptiveDuration = false,
  } = {},
) {
  if (duration === 0) return;
  const style = getComputedStyle(node);
  const opacity = +style.opacity;
  const primary_property = axis === "y" ? "height" : "width";
  const primary_property_value = parseFloat(style[primary_property]);
  const secondary_properties = axis === "y"
    ? ["top", "bottom"]
    : ["left", "right"];
  const capitalized_secondary_properties = secondary_properties.map(
    (e) => `${e[0].toUpperCase()}${e.slice(1)}`,
  );
  const padding_start_value = parseFloat(
    style[`padding${capitalized_secondary_properties[0]}`],
  );
  const padding_end_value = parseFloat(
    style[`padding${capitalized_secondary_properties[1]}`],
  );
  const margin_start_value = parseFloat(
    style[`margin${capitalized_secondary_properties[0]}`],
  );
  const margin_end_value = parseFloat(
    style[`margin${capitalized_secondary_properties[1]}`],
  );
  const border_width_start_value = parseFloat(
    style[`border${capitalized_secondary_properties[0]}Width`],
  );
  const border_width_end_value = parseFloat(
    style[`border${capitalized_secondary_properties[1]}Width`],
  );
  let prevT = 0;

  if (adaptiveDuration) {
    duration = Math.min(800, duration + (primary_property_value * .25));
  }

  return {
    delay,
    duration,
    easing,
    css: (t) => {
      const isTransitioningIn = t > prevT;
      const widthPart = isTransitioningIn ? 0.7 : 0.3;
      const otherPart = isTransitioningIn ? 0.3 : 0.7;

      const transitionLastPart = (t - widthPart) / otherPart;
      const scaleValue = transitionLastPart <= 0
        ? 0.995
        : 0.995 + transitionLastPart * 0.005;
      const opacityValue = transitionLastPart <= 0
        ? 0
        : transitionLastPart * opacity;
      const translateYValue = axis === "y"
        ? transitionLastPart <= 0 ? 2 : 2 - (transitionLastPart * 2)
        : 0;
      const translateXValue = axis === "x"
        ? transitionLastPart <= 0 ? 0 : 0 - (transitionLastPart * 2)
        : 0;
      const primaryPropertyFraction = Math.min(t / widthPart, 1);

      prevT = t;

      return `opacity: ${opacityValue};` +
        `overflow: ${overflow};` +
        `transform: scale(${scaleValue}) translate3d(-${translateXValue}px, -${translateYValue}px, 0);` +
        `${primary_property}: ${
          primaryPropertyFraction * primary_property_value
        }px;` +
        `padding-${secondary_properties[0]}: ${
          primaryPropertyFraction * padding_start_value
        }px;` +
        `padding-${secondary_properties[1]}: ${
          primaryPropertyFraction * padding_end_value
        }px;` +
        `margin-${secondary_properties[0]}: ${
          primaryPropertyFraction * margin_start_value
        }px;` +
        `margin-${secondary_properties[1]}: ${
          primaryPropertyFraction * margin_end_value
        }px;` +
        `border-${secondary_properties[0]}-width: ${
          t * border_width_start_value
        }px;` +
        `border-${secondary_properties[1]}-width: ${
          t * border_width_end_value
        }px;`;
    },
  };
}
